import {
  getApiSettingsGetAppointmentTypeSettings,
  getApiSettingsGetPayerEntryConfigsByType,
  getApiTemplateGetFieldList,
  getApiTemplateGetMasterTemplateList,
  getApiTemplateGetSectionTemplateList,
  getApiUserGetUsers,
  getApiTagGetTags,
  getApiOrgConfigurationGetServiceFacilities,
  getApiOrgConfigurationGetBillingProviderPtans,
  getApiOrgConfigurationGetRenderingProviders,
  getApiOrgConfigurationGetBillingProvidersByType,
  getApiFormGetForms,
} from 'endpoints';
import {
  PayerEntryConfigDTO,
  FormViewDTO,
  FormViewDTOPaginatedResponseDTO,
  ServiceFacilityDTO,
  BillingProviderPtanDTO,
  TemplateFieldDTO,
  TemplateListItemDTO,
} from 'dtos';
import {
  FormCategory,
} from 'dtos/formCategory';
import {
  normalizeAppointmentTypes,
  normalizeAppointmentTypeSettings,
} from 'views/EMR/Settings/helper';
import {
  GetQueryParams,
} from 'pages/Dashboard/types/common';
import {
  AppointmentTypes,
  AppointmentTypeSettings,
} from 'views/EMR/Settings/Appointments/types';
import {
  UserView,
} from 'views/EMR/Settings/Users';
import {
  RenderingProviderDTO,
} from 'dtos/renderingProviderDTO';
import {
  BillingProviderInfo,
} from 'views/EMR/Settings/BillingConfigurations/types';
import sortBy from 'lodash/sortBy';
import compact from 'lodash/compact';
import isFunction from 'lodash/isFunction';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';

export async function getAppointmentTypeSettings(): Promise<AppointmentTypeSettings[]> {
  const data = await getApiSettingsGetAppointmentTypeSettings();
  return normalizeAppointmentTypeSettings(data);
}

export async function getAppointmentTypes(): Promise<AppointmentTypes> {
  const data = await getApiSettingsGetAppointmentTypeSettings();
  return normalizeAppointmentTypes(data);
}

type FormsQueryParams = GetQueryParams & {
  formCategory?: FormCategory | null;
};

export async function getAllFormsByOrg({
  searchQuery,
  formCategory,
  setTotalSize,
  pagination,
  requestMedia,
}: FormsQueryParams): Promise<FormViewDTO[]> {
  const response = await getApiFormGetForms({
    searchText: searchQuery,
    category: formCategory ?? undefined,
    includeDrafts: true,
    page: pagination?.pageIndex ?? 0,
    pageSize: pagination?.pageSize ?? 25,
  });
  setTotalSize?.(response?.consentForms?.pageProperties?.totalSize ?? 0);
  const forms = response?.consentForms?.data ?? [];

  if (isFunction(requestMedia)) {
    const userIds = compact(forms.map(({ updatedByUser }) => updatedByUser?.userId));
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }

  return forms;
}

type LookupQueryParams = {
  search?: string;
  searchText?: string;
  page?: number;
  pageSize?: number;
  category?: FormCategory;
  includeDrafts?: boolean;
};

export async function getFormOptions({
  searchText,
  category,
  includeDrafts,
  page,
  pageSize,
}: LookupQueryParams): Promise<FormViewDTOPaginatedResponseDTO> {
  const response = await getApiFormGetForms({
    searchText,
    category,
    page: page ?? 0,
    includeDrafts,
    pageSize: pageSize ?? 25,
  });

  return response?.consentForms ?? { data: [], pageProperties: undefined };
}

export async function getPayerConfigurations({
  searchQuery: searchText,
  setTotalSize,
  pagination,
  configType,
  sortBy,
  sortOrder,
}: GetQueryParams): Promise<PayerEntryConfigDTO[]> {
  const response = await getApiSettingsGetPayerEntryConfigsByType({
    searchText,
    configType,
    sortBy,
    sortOrder,
    ...pagination,
  });
  setTotalSize(response?.pageProperties?.totalSize ?? 0);

  return [...(response.data ?? [])];
}

export async function getMasterTemplates({
  setTotalSize,
  searchQuery: searchText,
  pagination,
  requestMedia,
}: GetQueryParams): Promise<TemplateListItemDTO[]> {
  const response = await getApiTemplateGetMasterTemplateList({ searchText, ...pagination });
  setTotalSize(response?.pageProperties?.totalSize ?? 0);
  const data = response?.data ?? [];
  if (isFunction(requestMedia)) {
    const userIds = compact(data.map((item) => item.user?.userId));
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }
  return data;
}

export async function getSectionTemplates({
  setTotalSize,
  searchQuery: searchText,
  pagination,
  requestMedia,
}: GetQueryParams): Promise<TemplateListItemDTO[]> {
  const response = await getApiTemplateGetSectionTemplateList({ searchText, ...pagination });
  setTotalSize(response?.pageProperties?.totalSize ?? 0);
  const data = response?.data ?? [];
  if (isFunction(requestMedia)) {
    const userIds = compact(data.map((item) => item.user?.userId));
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }
  return data;
}

export async function getFields({
  setTotalSize,
  searchQuery: searchText,
  pagination,
  requestMedia,
}: GetQueryParams): Promise<TemplateFieldDTO[]> {
  const response = await getApiTemplateGetFieldList({ searchText, ...pagination });
  setTotalSize(response?.pageProperties?.totalSize ?? 0);
  const data = response?.data ?? [];
  if (isFunction(requestMedia)) {
    const userIds = compact(data.map((item) => item.user?.userId));
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }
  return data;
}

export async function getUsers(params?: GetQueryParams): Promise<UserView[]> {
  const requestMedia = params?.requestMedia;
  const response = await getApiUserGetUsers();
  const users = sortBy(
    response?.map(({
      title,
      phoneNumber,
      ...user
    }) => (
      {
        userTitle: {
          title: title ?? '',
          isRestricted: false,
        },
        phoneNumber: phoneNumber?.phoneNumberWithAreaCode,
        ...user,
        isActive: user?.isActive ?? true,
      }
    )) ?? [],
    (user) => !user?.isActive,
  );

  if (isFunction(requestMedia)) {
    const userIds = compact(users.map((user) => user.userId));
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }

  return users;
}

export async function getTags({
  searchQuery,
  setTotalSize,
  pagination,
  requestMedia,
}: GetQueryParams) {
  const response = await getApiTagGetTags({
    search: searchQuery,
    page: pagination?.pageIndex ?? 0,
    pageSize: pagination?.pageSize ?? 25,
  });
  setTotalSize?.(response?.pageProperties?.totalSize ?? 0);
  const tags = response?.data ?? [];

  if (isFunction(requestMedia)) {
    const userIds = compact(tags.map(({ user }) => user?.userId));
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }

  return tags;
}

export async function getServiceFacilities(): Promise<ServiceFacilityDTO[]> {
  const response = await getApiOrgConfigurationGetServiceFacilities({ includeStats: true });
  return response?.data ?? [];
}

export async function getRenderingProviders(): Promise<RenderingProviderDTO[]> {
  const response = await getApiOrgConfigurationGetRenderingProviders({ includeStats: true });
  return response?.data ?? [];
}

export async function getBillingProviders(): Promise<BillingProviderInfo[]> {
  const response = await getApiOrgConfigurationGetBillingProvidersByType({ includeStats: true });
  return [...(response?.organizations ?? []), ...(response?.individuals ?? [])];
}

export async function getBillingProviderPtans(): Promise<BillingProviderPtanDTO[]> {
  const response = await getApiOrgConfigurationGetBillingProviderPtans();
  return response?.data ?? [];
}
